import React, {useState} from 'react';
import { useTitle } from "fearless-common-ui";
import {ColumnDef} from "@tanstack/react-table";
import {adminRoutes} from "../../utils/routes";
import Pages from "../../components/layout/Pages";
import {Course, Deletable, UserAssignment} from "fearless-common";
import OrderableTable from "../../components/layout/OrderableTable";
import {backendEndpoints} from "../../libs/backendEndpoints";
import SearchInput from "../../components/SearchInput";
import Description from "../../components/Description";
import CellRender from "../../components/layout/table/CellRender";

/**
 * Column definitions for the courses table.
 * Sorting is enabled on all date and name columns.
 */
const columns: Array<ColumnDef<Course & Deletable & { user_assignments: UserAssignment[] }>> = [
    { id: 'id', accessorKey: 'id', header: 'ID', enableSorting: true },
    { id: 'name', accessorKey: 'name', header: 'Name', enableSorting: true },
    { id: 'starts_at', accessorKey: 'starts_at', header: 'Starts at', cell: CellRender.Date, enableSorting: true },
    { id: 'deadline', accessorKey: 'deadline', header: 'Deadline', cell: CellRender.Date, enableSorting: true },
    { id: 'user_assignments', accessorKey: 'user_assignments', header: '# Educators', cell: CellRender.Length, enableSorting: false },
    { id: 'created_at', accessorKey: 'created_at', header: 'Created at', cell: CellRender.Date, enableSorting: true },
    { id: 'deleted_at', accessorKey: 'deleted_at', header: 'Deleted at', cell: CellRender.Date, enableSorting: true },
]

/**
 * Admin page displaying a paginated, searchable list of all ELN courses.
 *
 * Supports:
 * - Free-text search by course name
 * - Filtering by archived status (active / archived)
 * - Sortable columns: id, name, starts_at, deadline, created_at, deleted_at
 *
 * Each row links to the {@link AdminCourse} detail page.
 */
export default function AdminCourses() {
    const [query, setQuery] = useState('');

    useTitle('Admin Courses');

    return (
        <Pages.Centred padded>
            <div className="d-flex align-items-center">
                <h1 className="flex-grow-1 mb-0">Courses</h1>
                <div className="ms-3">
                    <SearchInput onChange={(e) => setQuery(e.target.value)}/>
                </div>
            </div>
            <Description>
                All ELN courses created by educators.
            </Description>
            <OrderableTable
                filterables={[
                    {
                        field_name: 'deleted_at',
                        label: 'Archived',
                        options: [
                            { value: 'null', label: 'Active' },
                            { value: 'not null', label: 'Archived' },
                        ],
                    },
                ]}
                baseEndpoint={backendEndpoints.getAdminCourses}
                memoisedColumns={columns}
                buildLink={(record) => adminRoutes.course(record.id)}
                pagination
                searchQuery={query}
            />
        </Pages.Centred>
    );
}
